<div class="setup">
  <header class="setup-header">
    <div class="setup-title-row">
      <h1>Setup</h1>
      <div class="setup-domain-controls">
        <label class="domain-picker" *ngIf="domains.length">
          <span>Domain</span>
          <select [ngModel]="domain" (ngModelChange)="onDomainChange($event)">
            <option *ngFor="let d of domains" [value]="d">{{ d }}</option>
          </select>
        </label>
        <button type="button" class="btn-secondary" (click)="toggleAddDomain()">
          {{ showAddDomain ? 'Cancel' : '+ Add domain' }}
        </button>
      </div>
    </div>
    <p class="setup-subtitle">
      Take a domain from "just crawled" to "report-ready". Run all the prep steps in order, or run any single step.
    </p>
  </header>

  <!-- Add domain / competitor -->
  <section class="add-domain card" id="setup-add-panel" *ngIf="showAddDomain">
    <h2>{{ addMode === 'competitor' ? 'Add a competitor' : 'Add a domain' }}</h2>
    <p class="muted" *ngIf="addMode === 'competitor'">
      Crawl a new competitor of <strong>{{ domain }}</strong>. Scope which leaf sitemaps and how many URLs to crawl, then start.
      It will be marked as a competitor automatically; run <strong>topic prep</strong> from the Competitors card once its crawl completes.
    </p>
    <p class="muted" *ngIf="addMode !== 'competitor'">Check the sitemap, scope which leaf sitemaps and how many URLs to crawl, then start. Once the crawl completes, click <strong>Finish setup</strong>.</p>
    <div class="add-domain-row">
      <input
        type="text"
        placeholder="example.com"
        [(ngModel)]="newDomainInput"
        (keyup.enter)="estimateNewDomain()" />
      <button type="button" class="btn-secondary" (click)="estimateNewDomain()" [disabled]="estimateLoading">
        {{ estimateLoading ? 'Checking…' : 'Check sitemap' }}
      </button>
      <button
        type="button"
        class="btn-primary"
        (click)="startNewDomainCrawl()"
        [disabled]="startingCrawl || estimateLoading || !newDomainInput || (estimate && !hasSitemapSelection()) || (availableLocaleGroups.length > 0 && selectedLocales.size === 0)">
        {{ startingCrawl ? 'Starting…' : 'Start crawl' }}
      </button>
    </div>
    <p class="error" *ngIf="estimateError">{{ estimateError }}</p>

    <div class="bot-protected-panel" *ngIf="botProtected">
      <p class="bot-protected-title">This site appears to block automated sitemap requests (bot protection).</p>
      <p class="muted">
        Open the site in your browser, download the sitemap XML (often <code>/sitemap.xml</code> or linked from
        <code>/robots.txt</code>), then upload the file(s) here. You can select multiple <strong>.xml</strong> files at once.
        If you have a sitemap index, upload every referenced leaf file too.
      </p>
      <div class="bot-protected-actions">
        <label class="btn-secondary upload-btn">
          {{ estimateLoading ? 'Parsing…' : 'Upload sitemap file(s)' }}
          <input
            type="file"
            accept=".xml,.gz,application/xml,text/xml"
            multiple
            (change)="onSitemapFilesSelected($event)"
            [disabled]="estimateLoading || !newDomainInput" />
        </label>
      </div>
    </div>

    <div class="uploaded-sitemaps-note" *ngIf="usingUploadedSitemaps && uploadedSitemapFiles.length > 0">
      <span>
        Using {{ uploadedSitemapFiles.length }} uploaded file(s):
        {{ uploadedSitemapFileNames }}
      </span>
      <button type="button" class="btn-link" (click)="clearUploadedSitemaps()">Clear uploads</button>
    </div>

    <div class="estimate-summary" *ngIf="estimate as est">
      <p *ngIf="est.aliasApplied && est.canonicalDomain" class="estimate-line alias-note">
        Canonical domain: <strong>{{ est.canonicalDomain }}</strong>
        <span *ngIf="est.requestedDomain"> (from {{ est.requestedDomain }})</span>
      </p>
      <p *ngIf="est.sitemapSource === 'upload'" class="estimate-note">
        Sitemap loaded from your uploaded file(s) — remote fetch was skipped.
      </p>
      <div class="estimate-grid">
        <span><span class="estimate-label">Discovered URLs:</span> {{ est.totalPagesInSitemap | number }}<span *ngIf="est.sitemapParseTruncated" class="estimate-warn"> (parse truncated)</span></span>
        <span><span class="estimate-label">Selected URLs:</span> {{ getSelectedDiscoveredUrlCount() | number }}</span>
        <span><span class="estimate-label">Included at cap ({{ est.limit }}):</span> {{ getSelectedIncludedAtCap() | number }}</span>
        <span><span class="estimate-label">Est. crawl cost:</span> ${{ getSelectedEstimatedCost() | number: '1.2-2' }}</span>
      </div>
      <label class="checkline dev-bypass-cap" *ngIf="isDevView()">
        <input type="checkbox" [(ngModel)]="bypassCrawlCap" (ngModelChange)="onBypassCapChange()" />
        Bypass {{ defaultCrawlLimit }}-page cap (dev — uses up to 10,000)
      </label>

      <div *ngIf="(est.duplicateUrlsInSitemaps || 0) > 0" class="estimate-note">
        Duplicate URLs exist across sitemaps ({{ est.duplicateUrlsInSitemaps | number }}). Each unique URL is crawled once.
      </div>
      <div *ngIf="(est.sitemapParseFailures || []).length > 0" class="estimate-note warn">
        Parsed with warnings: {{ est.sitemapParseFailures?.length }} sitemap(s) failed to parse.
      </div>

      <div *ngIf="getLeafNodes(est.sitemapTree).length > 0" class="sitemap-controls">
        <div class="sitemap-controls-row">
          <label class="checkline">
            <input type="checkbox" [checked]="areAllSitemapsSelected()" (change)="setAllSitemapsSelected($any($event.target).checked)" />
            Select all leaf sitemaps
          </label>
        </div>
        <div class="sitemap-filter-row">
          <label class="strategy-label">
            Lastmod on/after:
            <input type="date" class="strategy-select" [ngModel]="minLastmodFrom" (ngModelChange)="onMinLastmodFromChange($event)" />
          </label>
          <label class="strategy-label">
            Per-sitemap cap mode:
            <select class="strategy-select" [ngModel]="perSitemapLimitMode" (ngModelChange)="onPerSitemapLimitModeChange($event)">
              <option value="recent">Most recent first</option>
              <option value="oldest">Oldest first</option>
              <option value="balanced">Balanced (newest + oldest)</option>
            </select>
          </label>
        </div>
        <div class="sitemap-filter-note">Per-sitemap cap applies after lastmod filtering using the selected mode.</div>

        <div class="sitemap-list">
          <div
            class="sitemap-item"
            *ngFor="let leaf of getLeafNodes(est.sitemapTree); trackBy: trackSitemap"
            [class.sitemap-item-disabled]="!selectedSitemaps.has(leaf.sitemapUrl)">
            <div class="sitemap-item-header">
              <label class="checkline">
                <input
                  type="checkbox"
                  [checked]="selectedSitemaps.has(leaf.sitemapUrl)"
                  (change)="toggleSitemapSelection(leaf.sitemapUrl, $any($event.target).checked)" />
                <span class="sitemap-url">{{ displaySitemapLabel(leaf.sitemapUrl) }}</span>
                <span class="sitemap-meta">({{ getLeafDisplayUrlCount(leaf) | number }} URLs)</span>
              </label>
              <label class="leaf-limit-label">
                Crawl
                <input
                  type="number"
                  min="1"
                  step="1"
                  class="leaf-limit-input"
                  [ngModel]="getPerSitemapLimit(leaf.sitemapUrl)"
                  (ngModelChange)="onPerSitemapLimitChange(leaf.sitemapUrl, $event)"
                  placeholder="all" />
                URLs
              </label>
            </div>
            <div class="sitemap-examples" *ngIf="(leaf.exampleUrls || []).length > 0">
              <span class="sitemap-examples-title">Examples:</span>
              <a
                *ngFor="let exampleUrl of (leaf.exampleUrls || []).slice(0, 2)"
                class="sitemap-example-link"
                [href]="exampleUrl"
                target="_blank"
                rel="noopener noreferrer">
                {{ exampleUrl }}
              </a>
            </div>
          </div>
        </div>
      </div>

      <div *ngIf="(est.localeGroups || []).length > 0" class="sitemap-controls locale-controls">
        <div class="locale-warning" [class.locale-warning-strong]="est.localeDetectionConfidence === 'heuristic'">
          <strong>Multiple languages/locales detected.</strong>
          <span *ngIf="est.localeDetectionConfidence === 'heuristic'">
            We detected these by URL pattern (this site doesn't split locales into separate sitemaps), so this is a best guess — review before crawling.
          </span>
          <span *ngIf="est.localeDetectionConfidence !== 'heuristic'">
            We detected these from the sitemap's hreflang annotations.
          </span>
          <span *ngIf="est.localePartitionedSitemaps">
            Leaf sitemaps are already split by language — non-selected locale sitemaps are unchecked automatically.
          </span>
          <strong>Only the checked locales are crawled — unchecked ones (greyed out) are excluded.</strong>
          By default we crawl just the default/no-locale pages.
        </div>
        <div class="sitemap-controls-row">
          <label class="checkline">
            <input type="checkbox" [checked]="areAllLocalesSelected()" (change)="setAllLocalesSelected($any($event.target).checked)" />
            Select all locales
          </label>
        </div>
        <div class="sitemap-list">
          <div
            class="sitemap-item"
            *ngFor="let group of est.localeGroups; trackBy: trackLocaleGroup"
            [class.sitemap-item-disabled]="!selectedLocales.has(group.key)">
            <div class="sitemap-item-header">
              <label class="checkline">
                <input
                  type="checkbox"
                  [checked]="selectedLocales.has(group.key)"
                  (change)="toggleLocaleSelection(group.key, $any($event.target).checked)" />
                <span class="sitemap-url">{{ group.label }}</span>
                <span class="sitemap-meta">({{ group.urlCount | number }} URLs)</span>
                <span class="locale-excluded-tag" *ngIf="!selectedLocales.has(group.key)">excluded</span>
              </label>
            </div>
            <div class="sitemap-examples" *ngIf="(group.exampleUrls || []).length > 0">
              <span class="sitemap-examples-title">Examples:</span>
              <a
                *ngFor="let exampleUrl of (group.exampleUrls || []).slice(0, 2)"
                class="sitemap-example-link"
                [href]="exampleUrl"
                target="_blank"
                rel="noopener noreferrer">
                {{ exampleUrl }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- No domain -->
  <section class="card" *ngIf="!domain && !showAddDomain">
    <p class="muted">No domain selected. Add a domain to get started.</p>
  </section>

  <ng-container *ngIf="domain">
    <!-- Progress + Finish setup -->
    <section class="setup-progress card">
      <div class="progress-row">
        <div class="progress-meta">
          <span class="progress-domain">{{ domain }}</span>
          <span class="progress-count">{{ completeCount }} / {{ totalCount }} steps complete</span>
        </div>
        <div class="progress-actions">
          <button type="button" class="btn-secondary" (click)="loadAll()" [disabled]="loading">Refresh</button>
          <button
            type="button"
            class="btn-primary"
            (click)="finishSetup()"
            [disabled]="runningAll || !!finishSetupBlockedReason"
            [title]="finishSetupBlockedReason || 'Queue all remaining processing steps'">
            {{ runningAll ? 'Queuing…' : 'Finish setup' }}
          </button>
        </div>
      </div>
      <div class="progress-bar">
        <div class="progress-fill" [style.width.%]="progressPercent"></div>
      </div>
      <p class="progress-blocked" *ngIf="finishSetupBlockedReason">{{ finishSetupBlockedReason }}</p>

      <div class="optin-toggles">
        <label>
          <input type="checkbox" [(ngModel)]="includeKeywords" />
          Also run est. keywords (Labs cost)
        </label>
        <label>
          <input type="checkbox" [(ngModel)]="includeOpportunities" />
          Also run supporting-page opportunities (needs sidecar)
        </label>
      </div>

      <div class="topics-map-scope" *ngIf="isDevView()">
        <div class="scope-head">
          <span class="scope-title">Dev: Topic map scope</span>
        </div>
        <div class="scope-options">
          <label>
            <input
              type="radio"
              name="topicsMapScope"
              value="all"
              [checked]="topicsMapScope === 'all'"
              (change)="onTopicsMapScopeChange('all')" />
            All domains (default)
          </label>
          <label>
            <input
              type="radio"
              name="topicsMapScope"
              value="selected"
              [checked]="topicsMapScope === 'selected'"
              (change)="onTopicsMapScopeChange('selected')" />
            Selected domains only
          </label>
        </div>

        <div class="scope-domains" *ngIf="topicsMapScope === 'selected'">
          <label *ngFor="let d of topicsMapCandidates" class="scope-domain">
            <input
              type="checkbox"
              [checked]="isTopicsMapDomainSelected(d)"
              (change)="toggleTopicsMapDomain(d)" />
            {{ d }}
          </label>
          <p class="step-note-warn">
            Dev only — rebuilds the workspace topic map from just these domains.
            All other domains' topic mappings are cleared until a full Topics map runs.
          </p>
        </div>
      </div>
    </section>

    <p class="error" *ngIf="error">{{ error }}</p>

    <!-- Core checklist -->
    <section class="steps card">
      <h2>Pipeline</h2>
      <div class="step-row" *ngFor="let step of coreSteps">
        <div class="step-main">
          <div class="step-head">
            <span class="step-title">{{ step.title }}</span>
            <span class="badge" [ngClass]="stateClass(step.state)">{{ stateLabel(step.state) }}</span>
          </div>
          <p class="step-desc">{{ step.description }}</p>
          <p class="step-detail" *ngIf="step.detail">{{ step.detail }}</p>
          <p class="step-note" *ngIf="step.note">{{ step.note }}</p>
          <p class="step-blocked" *ngIf="step.state === 'blocked' && step.blockedReason">{{ step.blockedReason }}</p>
        </div>
        <div class="step-action" *ngIf="step.actionable">
          <button
            type="button"
            class="btn-secondary btn-sm"
            (click)="runStep(step)"
            [disabled]="isStepActionDisabled(step)"
            [title]="stepActionDisabledReason(step) || step.actionLabel">
            {{ isBusy(step.key) ? 'Starting…' : step.actionLabel }}
          </button>
        </div>
      </div>
    </section>

    <!-- Opt-in steps -->
    <section class="steps card">
      <h2>Optional (opt-in)</h2>
      <p class="muted">These cost money or time and are not part of <em>Finish setup</em> unless toggled above.</p>
      <div class="step-row" *ngFor="let step of optionalSteps">
        <div class="step-main">
          <div class="step-head">
            <span class="step-title">{{ step.title }}</span>
            <span class="badge" [ngClass]="stateClass(step.state)">{{ stateLabel(step.state) }}</span>
          </div>
          <p class="step-desc">{{ step.description }}</p>
          <p class="step-detail" *ngIf="step.detail">{{ step.detail }}</p>
          <p class="step-note" *ngIf="step.note">{{ step.note }}</p>
          <p class="step-blocked" *ngIf="isSidecarBlocked(step)">
            Content-gap sidecar appears to be down. Start it with <code>npm run content-gap:serve</code>, then retry.
          </p>
          <p class="step-blocked" *ngIf="step.state === 'blocked' && !isSidecarBlocked(step) && step.blockedReason">
            {{ step.blockedReason }}
          </p>
        </div>
        <div class="step-action" *ngIf="step.actionable">
          <button
            type="button"
            class="btn-secondary btn-sm"
            (click)="runStep(step)"
            [disabled]="isStepActionDisabled(step)"
            [title]="stepActionDisabledReason(step) || step.actionLabel">
            {{ isBusy(step.key) ? 'Starting…' : step.actionLabel }}
          </button>
        </div>
      </div>
    </section>

    <!-- Competitors -->
    <section class="steps card competitors-card">
      <div class="competitors-head">
        <div>
          <h2>Competitors</h2>
          <p class="muted">Competitors of <strong>{{ domain }}</strong>. They need a crawl + minimal topic prep to feed this domain's supporting-page comparisons.</p>
        </div>
        <div class="competitors-actions">
          <button
            type="button"
            class="btn-secondary btn-sm"
            (click)="runCompetitorPrepAll()"
            [disabled]="runningCompetitorPrepAll || !hasPreppableCompetitors"
            title="Run topic prep for all crawled competitors (topics-map runs once for the batch)">
            {{ runningCompetitorPrepAll ? 'Queuing…' : 'Run topic prep for all' }}
          </button>
          <button type="button" class="btn-primary btn-sm" (click)="openAddDomain('competitor')">+ Add competitor</button>
        </div>
      </div>

      <p class="muted" *ngIf="competitors.length === 0">No competitors marked yet. Add one above, or mark an already-crawled domain below.</p>

      <div class="step-row" *ngFor="let c of competitors">
        <div class="step-main">
          <div class="step-head">
            <span class="step-title">{{ c.domain }}</span>
            <span class="badge" [ngClass]="competitorChip(c).cls">{{ competitorChip(c).label }}</span>
          </div>
          <p class="step-detail">{{ c.pagesAnalyzed | number }} pages analyzed · {{ c.aggregateCount | number }} topics</p>
          <p class="step-note step-note-warn" *ngIf="competitorContentBlocked(c)">
            No page content was captured — the site likely renders content client-side or blocks crawlers, so no topic signals could be extracted. Re-crawl to retry; if it persists, this domain can’t be prepped via the standard pipeline.
          </p>
          <p class="step-note" *ngIf="c.crawled && !c.hasTopicSignals && !competitorContentBlocked(c)">Still analyzing — topic prep unlocks once topic signals exist.</p>
          <p class="step-note" *ngIf="!c.crawled">Not crawled yet in this workspace. Crawl it (via “Add competitor”) before running topic prep.</p>
        </div>
        <div class="step-action competitor-action">
          <button
            type="button"
            class="btn-secondary btn-sm"
            (click)="runCompetitorPrep(c)"
            [disabled]="isCompetitorBusy(c.domain) || !canPrepCompetitor(c)">
            {{ isCompetitorBusy(c.domain) ? 'Working…' : 'Run topic prep' }}
          </button>
          <button
            type="button"
            class="btn-link btn-sm"
            (click)="removeCompetitor(c)"
            [disabled]="isCompetitorBusy(c.domain)">
            Remove
          </button>
        </div>
      </div>

      <div class="mark-existing-row">
        <span class="mark-existing-label">Mark an already-crawled domain:</span>
        <input
          type="text"
          placeholder="competitor.com"
          [(ngModel)]="markCompetitorInput"
          (keyup.enter)="markExistingCompetitor()" />
        <button type="button" class="btn-secondary btn-sm" (click)="markExistingCompetitor()" [disabled]="!markCompetitorInput">
          Mark competitor
        </button>
      </div>

      <p class="competitor-stale" *ngIf="competitorStatus?.staleForCompetitorSet">
        The competitor set changed since the last supporting-pages build — re-run “Supporting pages by topic” (and opportunities, if used) to include the current competitors.
      </p>
    </section>
  </ng-container>
</div>
